import { useState, useCallback, useRef, useMemo, useEffect } from "react";
import { BrowserRouter, Routes, Route, Link } from "react-router-dom";
import {
  Download,
  Check,
  Loader2,
  Bell,
  Brain,
  Zap,
  Coins,
  Cpu,
  Shield,
  Clock,
  ArrowUpRight,
  GitBranch,
  Terminal,
  Workflow,
  Code2,
  Lock,
  MessageSquare,
  Sparkles,
  Layers,
  Globe,
  FileCode2,
  KeyRound,
  ChevronDown,
  Github,
  Twitter,
} from "lucide-react";
import { Mockup } from "./components/landing/Mockup";
import { TheProblemVisual, TheSolutionVisual } from "./components/landing/ParallelAgentsVisual";
import { TokenCounter } from "./pages/TokenCounter";
import { ModelComparison } from "./pages/ModelComparison";
import { Tools } from "./pages/Tools";
import { NotFound } from "./pages/NotFound";
import { JsonFormatter } from "./pages/JsonFormatter";
import { Base64Tool } from "./pages/Base64Tool";
import { JwtDecoder } from "./pages/JwtDecoder";
import { JwtEncoder } from "./pages/JwtEncoder";
import { UuidGenerator } from "./pages/UuidGenerator";
import { UnixTimestamp } from "./pages/UnixTimestamp";
import { UrlEncodeDecode } from "./pages/UrlEncodeDecode";
import { HashGenerator } from "./pages/HashGenerator";
import { ColorConverter } from "./pages/ColorConverter";
import { RegexTester } from "./pages/RegexTester";
import { DiffTool } from "./pages/DiffTool";
import { MarkdownPreview } from "./pages/MarkdownPreview";
import { CronParser } from "./pages/CronParser";
import { BoxShadowGenerator } from "./pages/BoxShadowGenerator";
import { ApiTester } from "./pages/ApiTester";
import { Changelog } from "./pages/Changelog";
import "./index.css";

// ── Scroll-triggered animation hook ──────────────────────────
function useInView(options?: IntersectionObserverInit) {
  const ref = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry && entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.15, ...options }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return { ref, isVisible };
}

const DATA_API_URL = "https://ballah-data.isaiahballah.workers.dev";
const DOWNLOAD_SLUG = "ballah-code-canary";

/** Check if a downloadable build exists */
function useDownloadAvailable(): boolean | null {
  const [available, setAvailable] = useState<boolean | null>(null);

  useEffect(() => {
    fetch(`${DATA_API_URL}/download/${DOWNLOAD_SLUG}/info`)
      .then((res) => {
        setAvailable(res.ok);
      })
      .catch(() => {
        setAvailable(false);
      });
  }, []);

  return available;
}

// ── Apple logo — clean SF-style SVG ─────────────────────────
function AppleLogo({
  size = 13,
  className = "",
}: {
  size?: number;
  className?: string;
}) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      className={className}
      fill="currentColor"
    >
      <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z" />
    </svg>
  );
}

// ── Detect Mac architecture ──────────────────────────────────
function useChipType(): "apple-silicon" | "intel" | "unknown" {
  return useMemo(() => {
    if (typeof navigator === "undefined") return "unknown";
    const ua = navigator.userAgent;

    if (!/Mac/.test(ua)) return "unknown";

    try {
      const canvas = document.createElement("canvas");
      const gl = canvas.getContext("webgl2") ?? canvas.getContext("webgl");
      if (gl) {
        const dbg = gl.getExtension("WEBGL_debug_renderer_info");
        if (dbg) {
          const renderer = gl.getParameter(dbg.UNMASKED_RENDERER_WEBGL);
          if (/Apple/.test(renderer) && !/Intel/.test(renderer)) {
            return "apple-silicon";
          }
          if (/Intel/.test(renderer)) {
            return "intel";
          }
        }
      }
    } catch {
      // WebGL not available
    }

    if (navigator.platform === "MacIntel") {
      return "intel";
    }

    return "apple-silicon";
  }, []);
}

function chipLabel(chip: "apple-silicon" | "intel" | "unknown"): string {
  if (chip === "apple-silicon") return "Apple Silicon";
  if (chip === "intel") return "Intel";
  return "macOS";
}

// ── Gradient divider ─────────────────────────────────────────
function GradientDivider() {
  return (
    <div className="h-px bg-gradient-to-r from-transparent via-white/[0.06] to-transparent" />
  );
}

// ── Stipple dot divider ──────────────────────────────────────
function StippleDivider() {
  return (
    <div className="flex items-center justify-center gap-[6px] py-1">
      {Array.from({ length: 21 }).map((_, i) => {
        const dist = Math.abs(i - 10) / 10;
        return (
          <div
            key={i}
            className="w-[3px] h-[3px] rounded-full bg-white"
            style={{ opacity: 0.06 + (1 - dist) * 0.08 }}
          />
        );
      })}
    </div>
  );
}

// ── Interactive 3D Parallax Background ───────────────────────
function InteractiveBackground() {
  const gridRef = useRef<HTMLDivElement>(null);
  const orbsRef = useRef<HTMLDivElement>(null);
  const dustRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let rafId: number;
    let currentX = 0;
    let currentY = 0;
    let targetX = 0;
    let targetY = 0;

    const onMouseMove = (e: MouseEvent) => {
      targetX = (e.clientX / window.innerWidth) * 2 - 1;
      targetY = (e.clientY / window.innerHeight) * 2 - 1;
    };

    const animate = () => {
      currentX += (targetX - currentX) * 0.05;
      currentY += (targetY - currentY) * 0.05;
      
      if (gridRef.current) {
        gridRef.current.style.transform = `rotateX(60deg) translateY(-20%) translateZ(-200px) rotateY(${currentX * 6}deg) rotateX(${60 - currentY * 6}deg)`;
      }
      if (orbsRef.current) {
        orbsRef.current.style.transform = `translate(${currentX * -30}px, ${currentY * -30}px)`;
      }
      if (dustRef.current) {
        dustRef.current.style.transform = `translate(${currentX * -70}px, ${currentY * -70}px)`;
      }

      rafId = requestAnimationFrame(animate);
    };

    window.addEventListener("mousemove", onMouseMove, { passive: true });
    animate();

    return () => {
      window.removeEventListener("mousemove", onMouseMove);
      cancelAnimationFrame(rafId);
    };
  }, []);

  return (
    <div className="fixed inset-0 pointer-events-none overflow-hidden" style={{ zIndex: 0, perspective: '1200px' }}>
      {/* 3D Perspective Grid */}
      <div 
        ref={gridRef}
        className="absolute inset-[-50%] grid-lines opacity-[0.25]"
        style={{
          transformOrigin: 'top center',
          willChange: 'transform'
        }}
      />

      {/* Parallax Orbs */}
      <div 
        ref={orbsRef}
        className="absolute inset-0"
        style={{ willChange: 'transform' }}
      >
        <div className="absolute -top-[300px] left-1/2 w-[1000px] h-[1000px] rounded-full bg-violet-600/[0.06] blur-[180px] animate-orb-1 opacity-80" />
        <div className="absolute top-[30%] -left-[300px] w-[800px] h-[800px] rounded-full bg-purple-700/[0.05] blur-[160px] animate-orb-2 opacity-80" />
        <div className="absolute top-[60%] -right-[250px] w-[700px] h-[700px] rounded-full bg-violet-800/[0.06] blur-[150px] animate-orb-3 opacity-80" />
      </div>

      {/* Floating 3D Dust Particles */}
      <div 
        ref={dustRef}
        className="absolute inset-[-10%]"
        style={{ willChange: 'transform' }}
      >
        {Array.from({ length: 24 }).map((_, i) => (
          <div
            key={i}
            className="absolute rounded-full bg-violet-300"
            style={{
              top: `${Math.random() * 100}%`,
              left: `${Math.random() * 100}%`,
              width: `${Math.random() * 3 + 1}px`,
              height: `${Math.random() * 3 + 1}px`,
              opacity: Math.random() * 0.3 + 0.1,
              animation: `float-slow ${Math.random() * 4 + 6}s ease-in-out infinite`,
              animationDelay: `${Math.random() * -10}s`
            }}
          />
        ))}
      </div>
    </div>
  );
}

// ── Email CTA form (reusable) ────────────────────────────────
function EmailCTA({
  email,
  setEmail,
  status,
  errorMsg,
  onSubmit,
  downloadAvailable,
  chip,
}: {
  email: string;
  setEmail: (v: string) => void;
  status: "idle" | "loading" | "success" | "subscribed" | "error";
  errorMsg: string;
  onSubmit: () => void;
  downloadAvailable: boolean | null;
  chip: "apple-silicon" | "intel" | "unknown";
}) {
  if (status === "success") {
    return (
      <div className="flex flex-col items-center gap-4">
        <div className="text-sm text-white/60 inline-flex items-center gap-2">
          <Check size={15} className="text-green-400" /> Your download
          should start automatically.
        </div>
        <a
          href={`${DATA_API_URL}/download/${DOWNLOAD_SLUG}`}
          className="text-[12px] text-white/30 underline underline-offset-2 hover:text-white/50 transition-colors"
        >
          Download not starting? Click here.
        </a>

        {/* Installation instructions — glass card */}
        <div className="mt-2 max-w-md w-full rounded-2xl border border-white/[0.06] bg-white/[0.02] backdrop-blur-md shadow-[inset_0_1px_0_0_rgba(255,255,255,0.04)] px-6 py-5 text-left">
          <p className="text-[12px] font-medium text-white/50 mb-3">
            First launch on macOS
          </p>
          <ol className="text-[12px] text-white/35 leading-[1.8] list-decimal list-inside space-y-0.5">
            <li>
              Open the downloaded{" "}
              <code className="text-white/50">.dmg</code> and drag the app
              to Applications
            </li>
            <li>
              <strong className="text-white/50">Right-click</strong> the app
              → <strong className="text-white/50">Open</strong> (don't
              double-click)
            </li>
            <li>
              Click <strong className="text-white/50">"Open"</strong> on the
              dialog that appears
            </li>
          </ol>
          <p className="text-[11px] text-white/20 mt-3 leading-[1.6]">
            This is only needed once. macOS requires this for apps outside
            the App Store. We're working on full notarization.
          </p>
        </div>
      </div>
    );
  }

  if (status === "subscribed") {
    return (
      <div className="flex flex-col items-center gap-2">
        <div className="text-sm text-white/60 inline-flex items-center gap-2">
          <Check size={15} className="text-green-400" /> You're on the list.
        </div>
        <p className="text-[12px] text-white/30">
          We'll email you when the download is ready.
        </p>
      </div>
    );
  }

  return (
    <div>
      <div className="inline-flex items-center rounded-full overflow-hidden ring-1 ring-white/[0.08] shadow-[0_8px_40px_rgba(0,0,0,0.4)]">
        <input
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && onSubmit()}
          placeholder={
            downloadAvailable
              ? "your email to download"
              : "your email for early access"
          }
          disabled={status === "loading"}
          className="w-[280px] py-3.5 px-5 bg-white/[0.03] backdrop-blur-md border-r border-white/[0.06] rounded-l-full text-white/90 text-sm outline-none font-sans placeholder:text-white/25 focus:bg-white/[0.05] transition-all duration-300 disabled:opacity-50"
        />
        <button
          onClick={onSubmit}
          disabled={status === "loading" || !email}
          className="py-3.5 px-7 bg-gradient-to-r from-violet-600 to-violet-500 text-white text-sm font-semibold cursor-pointer flex items-center gap-2 font-sans hover:from-violet-500 hover:to-violet-400 transition-all duration-300 shadow-[0_0_24px_rgba(139,92,246,0.35),0_0_64px_rgba(139,92,246,0.12)] hover:shadow-[0_0_32px_rgba(139,92,246,0.5),0_0_80px_rgba(139,92,246,0.2)] disabled:opacity-50 disabled:cursor-not-allowed disabled:shadow-none"
        >
          {status === "loading" ? (
            <Loader2 size={14} className="animate-spin" />
          ) : downloadAvailable ? (
            <>
              <AppleLogo size={14} /> Download for Mac
            </>
          ) : (
            <>
              <Bell size={14} /> Notify me
            </>
          )}
        </button>
      </div>
      <p className="text-[12px] text-white/25 mt-4">
        {downloadAvailable
          ? `Free · macOS · ${chipLabel(chip)}`
          : "Free · macOS · Coming soon"}
      </p>
      {status === "error" && (
        <p className="text-[12px] text-red-400 mt-2">{errorMsg}</p>
      )}
    </div>
  );
}

export function App() {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState<
    "idle" | "loading" | "success" | "subscribed" | "error"
  >("idle");
  const [errorMsg, setErrorMsg] = useState("");
  const heroRef = useRef<HTMLElement>(null);
  const waveRef = useRef<HTMLDivElement>(null);
  const footerRef = useRef<HTMLElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const chip = useChipType();
  const downloadAvailable = useDownloadAvailable();
  const [scrolled, setScrolled] = useState(false);
  const mockupAnim = useInView();
  const problemAnim = useInView();
  const solutionAnim = useInView();
  const cascadeAnim = useInView();
  const howItWorksAnim = useInView();
  const comparisonAnim = useInView();
  const testimonialsAnim = useInView();
  const faqAnim = useInView();
  const statsAnim = useInView();
  const footerAnim = useInView({ threshold: 0.1 });

  // Bottom CTA state
  const [bottomEmail, setBottomEmail] = useState("");
  const [bottomStatus, setBottomStatus] = useState<
    "idle" | "loading" | "success" | "subscribed" | "error"
  >("idle");
  const [bottomErrorMsg, setBottomErrorMsg] = useState("");

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Overscroll rubber-band wave — only shows when pulling hard past page bottom
  useEffect(() => {
    let amount = 0;
    let decayRaf = 0;
    let wheelTimer: ReturnType<typeof setTimeout>;

    const apply = () => {
      if (!waveRef.current) return;
      const s = Math.min(1, Math.max(0, amount / 300));
      const lift = s * window.innerHeight * 0.8;
      // Lift ALL content up uniformly
      if (contentRef.current) {
        contentRef.current.style.transform = `translateY(-${lift}px)`;
      }
      // Footer stays 25% lower — counter-translate it back down
      if (footerRef.current) {
        footerRef.current.style.transform = `translateY(${lift * 0.25}px)`;
      }
      // Wave slides up from below — 100% = fully hidden, 0% = fully visible
      const waveSlide = 100 - (s * 100);
      waveRef.current.style.transform = `translateY(${waveSlide}%)`;
    };

    const decay = () => {
      amount *= 0.82; // fast snap back
      if (amount < 0.5) amount = 0;
      apply();
      if (amount > 0) decayRaf = requestAnimationFrame(decay);
    };

    const onWheel = (e: WheelEvent) => {
      const atBottom =
        Math.ceil(window.scrollY + window.innerHeight) >=
        document.documentElement.scrollHeight - 2;

      if (atBottom && e.deltaY > 0) {
        // Pulling past bottom — grow wave
        amount = Math.min(400, amount + e.deltaY * 0.8);
        apply();
        cancelAnimationFrame(decayRaf);
        clearTimeout(wheelTimer);
        // Snap back instantly
        clearTimeout(wheelTimer);
        wheelTimer = setTimeout(() => {
          decayRaf = requestAnimationFrame(decay);
        }, 0);
      } else if (amount > 0) {
        // Scrolling up while overscrolled — shrink wave
        amount = Math.max(0, amount + e.deltaY * 2);
        apply();
        if (amount <= 0) {
          amount = 0;
          apply();
        }
      }
    };

    window.addEventListener("wheel", onWheel, { passive: true });
    return () => {
      window.removeEventListener("wheel", onWheel);
      cancelAnimationFrame(decayRaf);
      clearTimeout(wheelTimer);
    };
  }, []);

  const handleSubmit = useCallback(async (targetEmail?: string, isBottom?: boolean) => {
    const theEmail = targetEmail ?? email;
    if (!theEmail) return;

    const setS = isBottom ? setBottomStatus : setStatus;
    const setE = isBottom ? setBottomErrorMsg : setErrorMsg;
    setS("loading");
    setE("");

    try {
      const res = await fetch(`${DATA_API_URL}/signup`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: theEmail,
          source: "ballah-code-web",
          metadata: { page: "landing", chip },
        }),
      });

      const data = await res.json();

      if (!res.ok || !data.ok) {
        setE(data.error ?? "Something went wrong");
        setS("error");
        return;
      }

      if (downloadAvailable) {
        setS("success");
        const a = document.createElement("a");
        a.href = `${DATA_API_URL}/download/${DOWNLOAD_SLUG}?email=${encodeURIComponent(theEmail)}`;
        a.download = "";
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
      } else {
        setS("subscribed");
      }
    } catch {
      setE("Couldn't connect. Try again.");
      setS("error");
    }
  }, [email, chip, downloadAvailable]);

  const scrollToHero = () => {
    heroRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <div className="min-h-screen bg-[#08090a] text-white font-sans">

      {/* ═══════════════════════════════════════════════════════
          BACKGROUND LAYERS (fixed, behind everything)
          ═══════════════════════════════════════════════════════ */}

      {/* Layer 1 — 3D Interactive Parallax Environment */}
      <InteractiveBackground />

      {/* Layer 3 — Noise grain (on top of everything for film texture) */}
      <div className="fixed inset-0 pointer-events-none" style={{ zIndex: 9999 }}>
        <svg className="w-full h-full opacity-[0.07]" xmlns="http://www.w3.org/2000/svg">
          <filter id="grain">
            <feTurbulence type="fractalNoise" baseFrequency="0.8" numOctaves="4" stitchTiles="stitch" />
            <feColorMatrix type="saturate" values="0" />
          </filter>
          <rect width="100%" height="100%" filter="url(#grain)" />
        </svg>
      </div>

      {/* ═══════════════════════════════════════════════════════
          CONTENT
          ═══════════════════════════════════════════════════════ */}
      {/* ─── Floating Nav — glass pill, always visible ────────────────── */}
      <nav className="fixed top-4 left-0 right-0 px-5" style={{ zIndex: 50 }}>
        <div className="max-w-[1100px] mx-auto flex items-center justify-between h-[52px] rounded-full bg-white/[0.04] backdrop-blur-2xl border border-white/[0.08] shadow-[0_2px_20px_rgba(0,0,0,0.3)] pl-5 pr-1.5">
          {/* Left — logo + links grouped together */}
          <div className="flex items-center gap-1">
            <span className="text-[15px] font-semibold tracking-[-0.03em] text-white/90 mr-4">
              ballah code
            </span>
            <Link
              to="/changelog"
              className="py-1.5 px-3.5 text-[13px] text-white/40 hover:text-white/90 transition-colors duration-200"
            >
              What's New
            </Link>
            <Link
              to="/tools"
              className="py-1.5 px-3.5 text-[13px] text-white/40 hover:text-white/90 transition-colors duration-200"
            >
              Tools
            </Link>
          </div>
          {/* Right — download pill */}
          <button
            onClick={scrollToHero}
            className="h-[38px] px-5 rounded-full bg-white/[0.07] text-[13px] font-medium text-white/70 flex items-center gap-2 hover:bg-white/[0.12] hover:text-white/90 transition-all duration-200 border border-white/[0.06]"
          >
            <Download size={13} /> Download
          </button>
        </div>
      </nav>

      <div ref={contentRef} className="relative bg-[#08090a]" style={{ zIndex: 3 }}>
        {/* Subtle Background Grid Line Overlay */}
        <div className="absolute inset-0 pointer-events-none light-grid z-[0]" />

        {/* ─── Hero ───────────────────────────────────────── */}
        <section
          ref={heroRef}
          className="max-w-[1100px] mx-auto px-8 pt-32 sm:pt-44 pb-20 text-center relative"
        >
          {/* Hero-local glow */}
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[600px] bg-[radial-gradient(ellipse,rgba(139,92,246,0.1)_0%,transparent_55%)] pointer-events-none" />
          <div className="absolute top-[15%] left-[35%] w-[500px] h-[400px] bg-[radial-gradient(ellipse,rgba(109,40,217,0.06)_0%,transparent_55%)] pointer-events-none" />

          {/* Beta badge */}
          <div className="animate-fade-up inline-flex items-center gap-2.5 mb-8 py-2 px-5 rounded-full border border-violet-500/15 bg-violet-500/[0.05] backdrop-blur-md text-[13px] text-violet-300/80 font-medium shadow-[inset_0_1px_0_0_rgba(167,139,250,0.06)]">
            <div className="w-[6px] h-[6px] rounded-full bg-violet-400 animate-pulse" />
            Currently in beta
          </div>

          {/* Headline — sans setup, serif payoff */}
          <h1
            className="animate-fade-up text-[clamp(48px,7.5vw,84px)] leading-[1.08] tracking-[-0.03em] mb-7"
            style={{ animationDelay: "80ms" }}
          >
            <span className="font-sans font-semibold bg-gradient-to-b from-white via-white/90 to-white/50 bg-clip-text text-transparent">
              Your AI tools keep
            </span>
            <br />
            <span className="font-serif font-normal italic tracking-[-0.01em] bg-gradient-to-r from-violet-300 to-purple-400 bg-clip-text text-transparent">
              forgetting what you said.
            </span>
          </h1>

          {/* Subtitle — sans-serif for contrast */}
          <p
            className="animate-fade-up text-[17px] text-white/40 leading-[1.7] max-w-2xl mx-auto mb-12 font-sans"
            style={{ animationDelay: "160ms" }}
          >
            Stop opening five different editor windows just to work on multiple features.
            <br className="hidden sm:block" />
            Ballah Code lets you run parallel tasks in one place, without ever losing context.
          </p>

          {/* CTA */}
          <div className="animate-fade-up" style={{ animationDelay: "240ms" }}>
            <EmailCTA
              email={email}
              setEmail={setEmail}
              status={status}
              errorMsg={errorMsg}
              onSubmit={() => handleSubmit()}
              downloadAvailable={downloadAvailable}
              chip={chip}
            />
          </div>
        </section>

        {/* ─── Mockup ──────────────────────────────────────── */}
        <section className="max-w-[1060px] mx-auto px-8 pt-4 pb-32 relative">
          {/* Atmospheric glow behind mockup */}
          <div className="absolute inset-x-0 top-1/2 -translate-y-1/2 h-[500px] bg-[radial-gradient(ellipse_60%_50%_at_50%_50%,rgba(139,92,246,0.1)_0%,rgba(109,40,217,0.05)_40%,transparent_70%)] pointer-events-none" />

          <div
            ref={mockupAnim.ref}
            className={`relative transition-all duration-1000 ease-out flex justify-center ${
              mockupAnim.isVisible
                ? "opacity-100 translate-y-0 scale-100"
                : "opacity-0 translate-y-10 scale-[0.97]"
            }`}
          >
            {/* Floating Badge 1 (Top Left) */}
            <div className="absolute -top-6 -left-4 z-20 animate-float-slow hidden md:flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/[0.04] border border-white/10 backdrop-blur-xl shadow-[0_8px_32px_rgba(0,0,0,0.5)]">
              <Sparkles size={12} className="text-violet-400" />
              <span className="text-[11px] font-mono text-white/70">Context parsed</span>
            </div>

            {/* Floating Badge 2 (Bottom Right) */}
            <div className="absolute -bottom-6 -right-4 z-20 animate-float-fast hidden md:flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#111113] border border-white/10 shadow-[0_8px_32px_rgba(0,0,0,0.5)]">
              <div className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse" />
              <span className="text-[11px] font-mono text-white/70">Background task running...</span>
            </div>

            <Mockup />
          </div>

        </section>

        {/* ─── Stats ribbon ────────────────────────────────── */}
        <section className="max-w-[900px] mx-auto px-8 pb-24">
          <div
            ref={statsAnim.ref}
            className={`grid grid-cols-2 md:grid-cols-4 gap-8 ${
              statsAnim.isVisible
                ? ""
                : ""
            }`}
          >
            {[
              { value: "14MB", label: "Total bundle", icon: Cpu },
              { value: "<100ms", label: "Cold start", icon: Clock },
              { value: "∞", label: "Parallel agents", icon: Zap },
              { value: "Zero", label: "Data stored", icon: Shield },
            ].map(({ value, label, icon: Icon }, i) => (
              <div
                key={i}
                className={`text-center group transition-all duration-700 ease-out ${
                  statsAnim.isVisible
                    ? "opacity-100 translate-y-0"
                    : "opacity-0 translate-y-6"
                }`}
                style={{
                  transitionDelay: statsAnim.isVisible ? `${i * 100}ms` : "0ms",
                }}
              >
                <div className="inline-flex items-center justify-center w-11 h-11 rounded-xl bg-violet-500/[0.06] border border-violet-400/[0.06] mb-3.5 group-hover:bg-violet-500/10 group-hover:border-violet-400/10 transition-all duration-300">
                  <Icon size={18} className="text-violet-400/60" />
                </div>
                <div className="text-[28px] font-semibold tracking-[-0.03em] text-white/90 mb-1 font-sans">
                  {value}
                </div>
                <div className="text-[13px] text-white/30">{label}</div>
              </div>
            ))}
          </div>
        </section>

        <StippleDivider />

        {/* ─── The problem ────────────────────────────────── */}
        <section>
          <div
            ref={problemAnim.ref}
            className={`max-w-[1060px] mx-auto px-8 py-28 transition-all duration-700 ease-out grid md:grid-cols-2 gap-16 md:gap-24 items-center ${
              problemAnim.isVisible
                ? "opacity-100 translate-y-0"
                : "opacity-0 translate-y-8"
            }`}
          >
            <div>
              <p className="text-[13px] text-violet-400/50 font-mono mb-5 tracking-wide uppercase">
                The problem
              </p>
              <h2 className="text-[clamp(30px,4.5vw,48px)] leading-[1.2] mb-8">
                <span className="font-sans font-semibold tracking-[-0.03em] text-white/90">One branch. One chat.</span>
                <br />
                <span className="font-serif font-normal italic tracking-[-0.01em] text-white/35">One thing at a time.</span>
              </h2>

              <div className="space-y-6 text-[17px] text-white/40 leading-[1.8] font-sans md:pr-4">
                <p>
                  You're switching tabs, stashing branches, and babysitting
                  agents that forget everything the moment the context window
                  fills. Want to fix a bug while a feature is in progress?
                  Stop the agent first.
                </p>
                <p>
                  The bottleneck isn't the model's intelligence — it's{" "}
                  <em className="font-serif italic text-white/70">your environment</em>.
                  Every tool assumes one human does one thing at a time.
                  That worked before agents. It doesn't anymore.
                </p>
              </div>
            </div>
            
            <div className="w-full relative mt-10 md:mt-0">
               <TheProblemVisual />
            </div>
          </div>
        </section>

        <StippleDivider />

        {/* ─── The answer ─────────────────────────────────── */}
        <section>
          <div
            ref={solutionAnim.ref}
            className={`max-w-[1060px] mx-auto px-8 py-28 transition-all duration-700 ease-out grid md:grid-cols-2 gap-16 md:gap-24 items-center ${
              solutionAnim.isVisible
                ? "opacity-100 translate-y-0"
                : "opacity-0 translate-y-8"
            }`}
          >
            {/* Animated UI on left on desktop */}
            <div className="w-full relative order-last md:order-first mt-10 md:mt-0">
               <TheSolutionVisual />
            </div>

            <div className="order-first md:order-last md:pl-8">
              {/* Accent line — violet gradient */}
              <div className="w-14 h-[2px] bg-gradient-to-r from-violet-500 to-purple-500 mb-10 rounded-full" />

              <h2 className="text-[clamp(30px,4.5vw,48px)] leading-[1.2] mb-8 text-white/90">
                <span className="font-sans font-semibold tracking-[-0.03em]">Built for </span>
                <span className="font-serif font-normal italic tracking-[-0.01em]">delegation.</span>
              </h2>

              <div className="space-y-6 text-[17px] text-white/40 leading-[1.8] font-sans">
                <p>
                  Multiple workspaces, multiple chats per workspace, each with
                  its own agent running independently. Hand off a feature, a
                  bug fix, a refactor — then move on to the next one.
                </p>
                <p>
                  Context persists across sessions.
                  Explain something once — <em className="font-serif not-italic text-white/70">it sticks</em>. No more re-pasting docs,
                  no more starting from zero when the context window fills.
                </p>
              </div>
            </div>
          </div>
        </section>

        <GradientDivider />

        {/* ─── How it works ────────────────────────────────── */}
        <section>
          <div
            ref={howItWorksAnim.ref}
            className={`max-w-[860px] mx-auto px-8 py-28 transition-all duration-700 ease-out ${
              howItWorksAnim.isVisible
                ? "opacity-100 translate-y-0"
                : "opacity-0 translate-y-8"
            }`}
          >
            <p className="text-[13px] text-violet-400/50 font-mono mb-5 tracking-wide uppercase text-center">
              How it works
            </p>
            <h2 className="text-[clamp(30px,4.5vw,48px)] leading-[1.2] mb-16 text-center">
              <span className="font-sans font-semibold tracking-[-0.03em] text-white/90">
                Three steps to{" "}
              </span>
              <span className="font-serif font-normal italic tracking-[-0.01em] text-white/90">
                parallel flow.
              </span>
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 relative">
              {/* Connector line */}
              <div className="hidden md:block absolute top-[44px] left-[16.67%] right-[16.67%] h-px bg-gradient-to-r from-violet-500/20 via-violet-500/10 to-violet-500/20" />

              {[
                {
                  step: "01",
                  title: "Describe",
                  desc: "Tell the architect what you need — a feature, a fix, a refactor. Plain language.",
                  icon: MessageSquare,
                },
                {
                  step: "02",
                  title: "Delegate",
                  desc: "The architect breaks it down and assigns agents. They work independently, in parallel.",
                  icon: GitBranch,
                },
                {
                  step: "03",
                  title: "Ship",
                  desc: "Review changes, approve what looks good, iterate on what doesn't. Stay in flow.",
                  icon: Sparkles,
                },
              ].map(({ step, title, desc, icon: Icon }, i) => (
                <div
                  key={i}
                  className={`text-center relative transition-all duration-700 ease-out ${
                    howItWorksAnim.isVisible
                      ? "opacity-100 translate-y-0"
                      : "opacity-0 translate-y-4"
                  }`}
                  style={{
                    transitionDelay: howItWorksAnim.isVisible
                      ? `${200 + i * 150}ms`
                      : "0ms",
                  }}
                >
                  <div className="relative z-10 w-[88px] h-[88px] rounded-2xl bg-white/[0.02] border border-white/[0.06] mx-auto mb-6 flex items-center justify-center shadow-[inset_0_1px_0_0_rgba(255,255,255,0.04)] hover:bg-white/[0.04] hover:border-white/[0.1] transition-all duration-300">
                    <Icon size={28} className="text-violet-400/60" />
                    <span className="absolute -top-2.5 -right-2.5 text-[11px] font-mono text-violet-400/40 bg-[#08090a] px-1.5 py-0.5 rounded-md border border-white/[0.06]">
                      {step}
                    </span>
                  </div>
                  <h3 className="text-[17px] font-semibold tracking-[-0.02em] mb-2 text-white/85">
                    {title}
                  </h3>
                  <p className="text-[14px] text-white/35 leading-[1.7] max-w-[240px] mx-auto">
                    {desc}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <GradientDivider />

        {/* ─── Feature cards — sticky split-screen ────────── */}
        <section className="max-w-[1060px] mx-auto px-8 py-32">
          <div className="flex flex-col md:flex-row gap-16 md:gap-24 relative items-start">
            
            {/* Sticky Left Column */}
            <div className="md:w-5/12 sticky top-32">
              <p className="text-[13px] text-violet-400/50 font-mono mb-5 tracking-wide uppercase">
                Core Pillars
              </p>
              <h2 className="text-[clamp(30px,4.5vw,48px)] leading-[1.1] mb-6">
                <span className="font-sans font-semibold tracking-[-0.03em] text-white/90">Everything else </span>
                <span className="font-serif font-normal italic tracking-[-0.01em] text-white/35">follows from that.</span>
              </h2>
              <p className="text-[17px] text-white/40 leading-[1.7] font-sans mb-8">
                When your tools don't hold you back, you can focus on the architecture. Ballah Code is built around three core pillars to multiply your output and keep you in flow.
              </p>
              <div className="hidden md:block w-14 h-[2px] bg-gradient-to-r from-violet-500/50 to-purple-500/0 rounded-full" />
            </div>

            {/* Scrolling Right Column (with Spotlight effect) */}
            <div className="md:w-7/12 flex flex-col gap-8 md:gap-12 relative" onMouseMove={(e) => {
              const rect = e.currentTarget.getBoundingClientRect();
              e.currentTarget.style.setProperty('--x', `${e.clientX - rect.left}px`);
              e.currentTarget.style.setProperty('--y', `${e.clientY - rect.top}px`);
            }}>
              {[
                {
                  icon: Brain,
                  title: "Remembers your context",
                  desc: "Context persists across sessions. Your architecture, your decisions, what worked — explain once, never repeat. The agent automatically retrieves past conversations and codebase context.",
                },
                {
                  icon: Zap,
                  title: "Delegates independently",
                  desc: "Hand off tasks like you would to a junior dev. The agent works in the background, making file changes and running tests — you don't have to wait or watch cursor movements.",
                },
                {
                  icon: Coins,
                  title: "Multiplies your output",
                  desc: "Multiple workspaces, multiple chats, multiple agents — all running at once. Start a refactor in one window while reviewing a feature in another. One developer, ten features in flight.",
                },
              ].map(({ icon: Icon, title, desc }, i) => (
                <div
                  key={i}
                  className="group relative rounded-3xl border border-white/[0.06] bg-[#111113] p-8 md:p-10 shadow-[inset_0_1px_0_0_rgba(255,255,255,0.04)] hover:shadow-[0_16px_60px_-16px_rgba(139,92,246,0.15)] hover:-translate-y-1 transition-all duration-500 cursor-default overflow-hidden"
                >
                  {/* Spotlight Gradient on Hover */}
                  <div 
                    className="pointer-events-none absolute -inset-px rounded-3xl opacity-0 transition duration-300 group-hover:opacity-100"
                    style={{
                      background: 'radial-gradient(600px circle at var(--x, 0) var(--y, 0), rgba(139, 92, 246, 0.1), transparent 40%)'
                    }}
                  />
                  
                  <div className="relative z-10">
                    <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-violet-500/15 to-violet-500/[0.03] border border-violet-400/[0.08] flex items-center justify-center group-hover:from-violet-500/25 group-hover:border-violet-400/15 transition-all duration-500 mb-8">
                      <Icon size={26} className="text-violet-400/80" />
                    </div>
                    <h3 className="text-[20px] font-semibold tracking-[-0.02em] mb-4 text-white/90">
                      {title}
                    </h3>
                    <p className="text-[16px] text-white/40 leading-[1.7]">
                      {desc}
                    </p>
                  </div>
                </div>
              ))}
            </div>
            
          </div>
        </section>

        {/* ─── Stack (icon pills) ─────────────────────────── */}
        <section className="max-w-[1060px] mx-auto px-8 py-16 text-center">
          <div className="flex justify-center gap-3 flex-wrap">
            {[
              { icon: Layers, label: "Electrobun" },
              { icon: Terminal, label: "Bun runtime" },
              { icon: Cpu, label: "14MB bundle" },
              { icon: Globe, label: "Native webview" },
              { icon: FileCode2, label: "TypeScript" },
              { icon: KeyRound, label: "Bring your own keys" },
            ].map(({ icon: Icon, label }, i) => (
              <span
                key={i}
                className="group py-1.5 px-4 rounded-full bg-white/[0.02] border border-white/[0.05] backdrop-blur-sm shadow-[inset_0_1px_0_0_rgba(255,255,255,0.03)] text-[13px] text-white/30 hover:bg-white/[0.04] hover:text-white/45 transition-all duration-200 inline-flex items-center gap-1.5"
              >
                <Icon size={12} className="opacity-50 group-hover:opacity-80 transition-opacity" />
                {label}
              </span>
            ))}
          </div>
        </section>

        <GradientDivider />

        {/* ─── Why Ballah Code — comparison ────────────────── */}
        <section>
          <div
            ref={comparisonAnim.ref}
            className={`max-w-[780px] mx-auto px-8 py-28 transition-all duration-700 ease-out ${
              comparisonAnim.isVisible
                ? "opacity-100 translate-y-0"
                : "opacity-0 translate-y-8"
            }`}
          >
            <p className="text-[13px] text-violet-400/50 font-mono mb-5 tracking-wide uppercase text-center">
              Why Ballah Code
            </p>
            <h2 className="text-[clamp(30px,4.5vw,48px)] leading-[1.2] mb-14 text-center">
              <span className="font-sans font-semibold tracking-[-0.03em] text-white/90">
                Not another{" "}
              </span>
              <span className="font-serif font-normal italic tracking-[-0.01em] text-white/90">
                chat wrapper.
              </span>
            </h2>

            <div className="rounded-2xl border border-white/[0.06] bg-white/[0.01] overflow-hidden">
              {/* Header */}
              <div className="grid grid-cols-[1fr_80px_80px_80px] sm:grid-cols-[1fr_100px_100px_100px] bg-white/[0.03] border-b border-white/[0.06] py-3 px-5 text-[11px] font-mono text-white/30 uppercase tracking-wider">
                <span />
                <span className="text-center text-violet-400/70 font-semibold">
                  Ballah
                </span>
                <span className="text-center">Cursor</span>
                <span className="text-center">Windsurf</span>
              </div>
              {/* Rows */}
              {[
                { feature: "Parallel agents", ballah: true, cursor: false, windsurf: false },
                { feature: "Persistent context", ballah: true, cursor: false, windsurf: false },
                { feature: "Multi-workspace", ballah: true, cursor: false, windsurf: false },
                { feature: "Bring your own keys", ballah: true, cursor: false, windsurf: true },
                { feature: "Architect → worker model", ballah: true, cursor: false, windsurf: false },
                { feature: "Native (not Electron)", ballah: true, cursor: false, windsurf: false },
                { feature: "< 20MB install", ballah: true, cursor: false, windsurf: false },
              ].map(({ feature, ballah, cursor, windsurf }, i) => (
                <div
                  key={i}
                  className={`grid grid-cols-[1fr_80px_80px_80px] sm:grid-cols-[1fr_100px_100px_100px] py-3 px-5 text-[13px] ${
                    i % 2 === 0 ? "bg-white/[0.015]" : "bg-transparent"
                  } border-b border-white/[0.03] last:border-0`}
                >
                  <span className="text-white/40">{feature}</span>
                  <span className="text-center flex justify-center">
                    {ballah ? (
                      <Check size={14} className="text-violet-400" />
                    ) : (
                      <span className="text-white/10">—</span>
                    )}
                  </span>
                  <span className="text-center flex justify-center">
                    {cursor ? (
                      <Check size={14} className="text-white/30" />
                    ) : (
                      <span className="text-white/10">—</span>
                    )}
                  </span>
                  <span className="text-center flex justify-center">
                    {windsurf ? (
                      <Check size={14} className="text-white/30" />
                    ) : (
                      <span className="text-white/10">—</span>
                    )}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </section>

        <GradientDivider />

        {/* ─── FAQ ──────────────────────────────────────────── */}
        <section>
          <div
            ref={faqAnim.ref}
            className={`max-w-[640px] mx-auto px-8 py-28 transition-all duration-700 ease-out ${
              faqAnim.isVisible
                ? "opacity-100 translate-y-0"
                : "opacity-0 translate-y-8"
            }`}
          >
            <h2 className="text-[clamp(24px,3.5vw,36px)] leading-[1.2] mb-12 text-center">
              <span className="font-sans font-semibold tracking-[-0.03em] text-white/90">
                Common{" "}
              </span>
              <span className="font-serif font-normal italic tracking-[-0.01em] text-white/90">
                questions.
              </span>
            </h2>

            <div className="space-y-2">
              {[
                {
                  q: "Is Ballah Code free?",
                  a: "Yes. Ballah Code is free to download and use. You bring your own API keys for the AI models — so you only pay for what you actually consume.",
                },
                {
                  q: "Which AI models are supported?",
                  a: "Any model with an API. Claude, GPT, Gemini, open-source models — bring your own keys and switch models per task. The architect can delegate to different models for different sub-tasks.",
                },
                {
                  q: "How is this different from Cursor or Windsurf?",
                  a: "Ballah Code is built around parallel delegation. Instead of one chat doing one thing, you can run multiple agents across multiple workspaces simultaneously. Context persists across sessions — you never start from zero.",
                },
                {
                  q: "What platforms are supported?",
                  a: "macOS (Apple Silicon and Intel). Windows and Linux support is on the roadmap.",
                },
                {
                  q: "Is my code sent to your servers?",
                  a: "No. Your code goes directly to the AI provider you've configured (Anthropic, OpenAI, Google, etc). Ballah Code doesn't proxy, log, or store your code. Your keys, your data.",
                },
              ].map(({ q, a }, i) => (
                <details
                  key={i}
                  className="group rounded-xl border border-white/[0.06] bg-white/[0.01] overflow-hidden hover:border-white/[0.1] transition-colors duration-200"
                >
                  <summary className="flex items-center justify-between cursor-pointer py-4 px-5 text-[15px] text-white/60 font-medium hover:text-white/80 transition-colors list-none [&::-webkit-details-marker]:hidden">
                    {q}
                    <ChevronDown
                      size={16}
                      className="text-white/20 group-open:rotate-180 transition-transform duration-200 shrink-0 ml-4"
                    />
                  </summary>
                  <div className="px-5 pb-4 text-[14px] text-white/35 leading-[1.75]">
                    {a}
                  </div>
                </details>
              ))}
            </div>
          </div>
        </section>

        <GradientDivider />

        {/* ─── Final CTA ──────────────────────────────────── */}
        <section className="max-w-[1060px] mx-auto px-8 pt-40 pb-40 text-center relative">
          {/* Glow — violet only */}
          <div className="absolute top-[10%] left-1/2 -translate-x-1/2 w-[700px] h-[500px] bg-[radial-gradient(ellipse,rgba(139,92,246,0.08)_0%,rgba(109,40,217,0.04)_40%,transparent_65%)] pointer-events-none" />

          <h2 className="text-[clamp(32px,5.5vw,56px)] leading-[1.1] mb-5 relative">
            <span className="font-sans font-semibold tracking-[-0.03em] bg-gradient-to-b from-white to-white/60 bg-clip-text text-transparent">Ship faster. </span>
            <span className="font-serif font-normal italic tracking-[-0.01em] bg-gradient-to-b from-white via-white/80 to-white/40 bg-clip-text text-transparent">Delegate more.</span>
          </h2>
          <p className="text-[15px] text-white/35 mb-12 relative font-sans">
            Bring your own keys. Keep your context. Run in parallel.
          </p>

          <div className="relative">
            <EmailCTA
              email={bottomEmail}
              setEmail={setBottomEmail}
              status={bottomStatus}
              errorMsg={bottomErrorMsg}
              onSubmit={() => handleSubmit(bottomEmail, true)}
              downloadAvailable={downloadAvailable}
              chip={chip}
            />
          </div>
        </section>

        {/* ─── Footer — Animated ────────────────────────── */}
        <footer ref={footerRef} className="border-t-0 relative z-10">
          <div ref={footerAnim.ref} className="max-w-[1060px] mx-auto px-8 pt-16 pb-10">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-10 mb-16 font-mono">
              {[
                {
                  title: "Product",
                  links: [
                    { label: "Download", action: scrollToHero },
                    { label: "Release Notes", to: "/changelog" },
                    { label: "Pricing", href: "#" },
                    { label: "Documentation", href: "#" },
                  ],
                },
                {
                  title: "Resources",
                  links: [
                    { label: "Developer Tools", to: "/tools" },
                    { label: "Token Counter", to: "/tools/llm-token-counter" },
                    { label: "Model Comparison", to: "/tools/ai-model-comparison" },
                    { label: "Security", href: "#" },
                  ],
                },
                {
                  title: "Company",
                  links: [
                    { label: "About", href: "#" },
                    { label: "Privacy", href: "#" },
                    { label: "Terms", href: "#" },
                  ],
                },
                {
                  title: "Connect",
                  links: [
                    { label: "GitHub", href: "https://github.com/isaiahb/ballah-code", icon: Github, external: true },
                    { label: "X", href: "#", icon: Twitter },
                  ],
                },
              ].map((column: any, colIdx) => (
                <div
                  key={colIdx}
                  className={`transition-all duration-700 ease-out ${
                    footerAnim.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
                  }`}
                  style={{ transitionDelay: footerAnim.isVisible ? `${colIdx * 100}ms` : "0ms" }}
                >
                  <span className="text-[11px] font-medium text-white/50 uppercase tracking-[0.1em] block mb-4">
                    {column.title}
                  </span>
                  <ul className="space-y-2.5 text-[12px] text-white/30 uppercase tracking-[0.06em]">
                    {column.links.map((link: any, linkIdx: number) => (
                      <li key={linkIdx}>
                        {link.action ? (
                          <a href="#" onClick={(e) => { e.preventDefault(); link.action(); }} className="hover:text-white/70 transition-colors inline-flex items-center gap-1.5">
                            {link.icon && <link.icon size={12} />} {link.label}
                          </a>
                        ) : link.to ? (
                          <Link to={link.to} className="hover:text-white/70 transition-colors inline-flex items-center gap-1.5">
                            {link.icon && <link.icon size={12} />} {link.label}
                          </Link>
                        ) : (
                          <a href={link.href} target={link.external ? "_blank" : undefined} rel={link.external ? "noopener noreferrer" : undefined} className="hover:text-white/70 transition-colors inline-flex items-center gap-1.5">
                            {link.icon && <link.icon size={12} />} {link.label}
                          </a>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
            
            {/* Bottom Footer Details */}
            <div 
              className={`flex flex-col sm:flex-row items-center justify-between gap-3 font-mono transition-all duration-1000 ease-out border-t border-white/[0.04] pt-8 ${
                footerAnim.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
              }`}
              style={{ transitionDelay: footerAnim.isVisible ? `400ms` : "0ms" }}
            >
              <span className="text-[11px] text-white/20 uppercase tracking-[0.08em]">
                Copyright &copy; {new Date().getFullYear()} Ballah Tech
              </span>
              <span className="text-[11px] text-white/20 uppercase tracking-[0.08em]">
                Current Status: Beta
              </span>
            </div>
          </div>
        </footer>

      </div>

      {/* ═══════════════════════════════════════════════════════
          GRADIENT WAVE — fixed to viewport bottom, z:2
          Sits ABOVE the content wrapper (z:1) so it paints
          over the dark page background.
          ═══════════════════════════════════════════════════════ */}
      <div
        ref={waveRef}
        className="fixed bottom-0 left-0 right-0 pointer-events-none"
        style={{
          height: "80vh",
          zIndex: 2,
          transform: "translateY(100%)",
        }}
      >
        {/* Solid base color — extends far above to fill any rebound gap */}
        <div className="absolute left-0 right-0 bottom-0" style={{ height: "200vh", background: "#08090a" }} />

        {/* Dia-style blurred stepped columns — brand violet/purple/pink/silver */}
        <div className="absolute bottom-0 left-[-5%] right-[-5%] flex items-end" style={{ height: "65%", filter: "blur(18px)" }}>
          {[
            { h: "18%", bg: "linear-gradient(to top, #2e1065, #4c1d95 40%, #6d28d9 70%, transparent)" },
            { h: "28%", bg: "linear-gradient(to top, #3b0764, #5b21b6 30%, #7c3aed 55%, #a78bfa 80%, transparent)" },
            { h: "38%", bg: "linear-gradient(to top, #2e1065, #6d28d9 25%, #8b5cf6 50%, #c084fc 75%, transparent)" },
            { h: "52%", bg: "linear-gradient(to top, #3b0764, #5b21b6 20%, #8b5cf6 40%, #c084fc 60%, #e9d5ff 80%, transparent)" },
            { h: "45%", bg: "linear-gradient(to top, #2e1065, #7c3aed 20%, #a78bfa 42%, #d8b4fe 65%, #f3e8ff 85%, transparent)" },
            { h: "62%", bg: "linear-gradient(to top, #3b0764, #5b21b6 15%, #8b5cf6 32%, #c084fc 48%, #d946ef 65%, #f0abfc 82%, transparent)" },
            { h: "55%", bg: "linear-gradient(to top, #2e1065, #6d28d9 18%, #a78bfa 38%, #c084fc 55%, #ec4899 72%, #f9a8d4 90%, transparent)" },
            { h: "70%", bg: "linear-gradient(to top, #3b0764, #5b21b6 12%, #7c3aed 28%, #a78bfa 42%, #d946ef 55%, #f0abfc 68%, #e4e4e7 84%, transparent)" },
            { h: "65%", bg: "linear-gradient(to top, #2e1065, #6d28d9 15%, #8b5cf6 30%, #c084fc 48%, #ec4899 62%, #f9a8d4 78%, #d4d4d8 92%, transparent)" },
            { h: "80%", bg: "linear-gradient(to top, #3b0764, #4c1d95 10%, #7c3aed 22%, #a78bfa 36%, #c084fc 48%, #d946ef 58%, #f0abfc 70%, #e4e4e7 84%, transparent)" },
            { h: "90%", bg: "linear-gradient(to top, #2e1065, #5b21b6 8%, #7c3aed 18%, #a78bfa 30%, #c084fc 42%, #d946ef 54%, #f0abfc 66%, #f9a8d4 78%, #e4e4e7 90%, transparent)" },
            { h: "100%", bg: "linear-gradient(to top, #3b0764, #4c1d95 8%, #6d28d9 16%, #8b5cf6 26%, #a78bfa 36%, #c084fc 46%, #d946ef 56%, #f0abfc 66%, #f9a8d4 76%, #e4e4e7 86%, #f4f4f5 94%, transparent)" },
            { h: "92%", bg: "linear-gradient(to top, #2e1065, #5b21b6 10%, #7c3aed 22%, #a78bfa 34%, #c084fc 46%, #d946ef 58%, #f0abfc 70%, #e4e4e7 84%, transparent)" },
            { h: "82%", bg: "linear-gradient(to top, #3b0764, #6d28d9 12%, #8b5cf6 26%, #c084fc 40%, #d946ef 54%, #f0abfc 68%, #f9a8d4 80%, #d4d4d8 92%, transparent)" },
            { h: "68%", bg: "linear-gradient(to top, #2e1065, #5b21b6 14%, #7c3aed 30%, #a78bfa 46%, #d946ef 60%, #ec4899 74%, #e4e4e7 88%, transparent)" },
            { h: "72%", bg: "linear-gradient(to top, #3b0764, #6d28d9 15%, #8b5cf6 32%, #c084fc 48%, #ec4899 64%, #f9a8d4 78%, #d4d4d8 90%, transparent)" },
            { h: "58%", bg: "linear-gradient(to top, #2e1065, #5b21b6 18%, #a78bfa 38%, #c084fc 55%, #d946ef 72%, #f0abfc 88%, transparent)" },
            { h: "48%", bg: "linear-gradient(to top, #3b0764, #7c3aed 20%, #a78bfa 42%, #c084fc 62%, #d946ef 82%, transparent)" },
            { h: "55%", bg: "linear-gradient(to top, #2e1065, #6d28d9 18%, #8b5cf6 38%, #c084fc 58%, #ec4899 78%, transparent)" },
            { h: "40%", bg: "linear-gradient(to top, #3b0764, #5b21b6 22%, #8b5cf6 48%, #a78bfa 72%, transparent)" },
            { h: "30%", bg: "linear-gradient(to top, #2e1065, #6d28d9 30%, #7c3aed 55%, #a78bfa 80%, transparent)" },
            { h: "20%", bg: "linear-gradient(to top, #3b0764, #4c1d95 40%, #6d28d9 70%, transparent)" },
          ].map((col, i) => (
            <div
              key={i}
              style={{
                flex: 1,
                height: col.h,
                background: col.bg,
              }}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

// ── Router ───────────────────────────────────────────────────

function AppRouter() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<App />} />
        <Route path="/tools" element={<Tools />} />
        <Route path="/tools/llm-token-counter" element={<TokenCounter />} />
        <Route path="/tools/ai-model-comparison" element={<ModelComparison />} />
        <Route path="/tools/json-formatter" element={<JsonFormatter />} />
        <Route path="/tools/base64" element={<Base64Tool />} />
        <Route path="/tools/jwt-decoder" element={<JwtDecoder />} />
        <Route path="/tools/jwt-encoder" element={<JwtEncoder />} />
        <Route path="/tools/uuid-generator" element={<UuidGenerator />} />
        <Route path="/tools/unix-timestamp" element={<UnixTimestamp />} />
        <Route path="/tools/url-encode-decode" element={<UrlEncodeDecode />} />
        <Route path="/tools/hash-generator" element={<HashGenerator />} />
        <Route path="/tools/color-converter" element={<ColorConverter />} />
        <Route path="/tools/regex-tester" element={<RegexTester />} />
        <Route path="/tools/diff-checker" element={<DiffTool />} />
        <Route path="/tools/markdown-preview" element={<MarkdownPreview />} />
        <Route path="/tools/cron-parser" element={<CronParser />} />
        <Route path="/tools/box-shadow-generator" element={<BoxShadowGenerator />} />
        <Route path="/tools/api-tester" element={<ApiTester />} />
        <Route path="/changelog" element={<Changelog />} />
        <Route path="*" element={<NotFound />} />
      </Routes>
    </BrowserRouter>
  );
}

export default AppRouter;
